import { useState } from 'react';
import { Truck } from 'lucide-react';
import { Button, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Card, CardContent, CardHeader, CardTitle, Spinner } from '@kamalkari/ui';
import { formatPrice, COUNTRIES } from '@kamalkari/utils';
import { useCartStore } from '@/stores/cartStore';

const API_URL = import.meta.env.VITE_API_URL || 'https://0vkok222yh.execute-api.us-east-1.amazonaws.com';

interface ShippingRate {
  id: string;
  carrier: string;
  service: string;
  price: number;
  estimatedDays: number;
}

const carrierLogos: Record<string, string> = {
  USPS: 'USPS',
  UPS: 'UPS',
  FedEx: 'FedEx',
  DHL: 'DHL',
};

export function ShippingCalculator() {
  const { items } = useCartStore();
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('US');
  const [rates, setRates] = useState<ShippingRate[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const calculateRates = async () => {
    if (!zip) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/v1/shipping/rates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: items.map((item) => ({
            weight: 8, // Default weight in oz
            quantity: item.quantity,
          })),
          destination: {
            street1: '123 Main St', // Placeholder for estimation
            city: 'New York',
            state: 'NY',
            zip,
            country,
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to calculate shipping rates');
      }

      const data = await response.json();
      setRates(data.rates || []);
    } catch (err) {
      setError('Unable to calculate shipping rates. Please try again.');
      setRates(null);
    } finally {
      setIsLoading(false);
    }
  };

  if (items.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Truck className="w-5 h-5" />
          Shipping Estimate
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input
              placeholder="ZIP / Postal Code"
              value={zip}
              onChange={(e) => setZip(e.target.value)}
            />
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            onClick={calculateRates}
            variant="outline"
            className="w-full"
            disabled={isLoading || !zip}
          >
            {isLoading ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Calculating...
              </>
            ) : (
              'Calculate Shipping'
            )}
          </Button>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          {rates && rates.length > 0 && (
            <div className="space-y-2 pt-2">
              {rates.map((rate) => (
                <div
                  key={rate.id}
                  className="flex items-center justify-between p-3 bg-warm-50 rounded-sm text-sm"
                >
                  <div>
                    <p className="font-medium text-warm-900">
                      {carrierLogos[rate.carrier] || rate.carrier} {rate.service}
                    </p>
                    <p className="text-warm-500 text-xs">{rate.estimatedDays} business days</p>
                  </div>
                  <span className="font-medium">{formatPrice(rate.price)}</span>
                </div>
              ))}
            </div>
          )}

          {rates && rates.length === 0 && (
            <p className="text-sm text-warm-600">No shipping options available for this destination.</p>
          )}

          <p className="text-xs text-warm-500">
            Rates shown are estimates. Final rates calculated at checkout.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
